/**
 * Pure selection logic for the section-shadowing supply side: decide what a
 * reviewer can claim (from the shadowable candidates, each annotated with
 * whether its week is guaranteed, whether it is shadowable yet, and its current
 * coverage) and how a reviewer stands against their per-guaranteed-week review
 * requirement.
 *
 * Supply is pull-based: assignments are created only when a reviewer opts in
 * (a required review or overflow), and the requirement is the demand signal
 * that drives that. Kept free of Firestore so the rules are unit-testable; the
 * Cloud Functions read the docs, build the inputs, call these, then execute the
 * writes.
 */
import type {SectionId, SectionWeekId} from './sectionApi';

/** Required reviews per guaranteed week when a course has not configured it. */
export const DEFAULT_REQUIRED_SHADOW_REVIEWS_PER_WEEK = 1;

/**
 * Resolves a course's configured number of required reviews per guaranteed
 * week.
 *
 * @param configured - The stored config value, or undefined when the course
 *   has not set one.
 * @return The configured value when it is a positive integer, otherwise
 *   `DEFAULT_REQUIRED_SHADOW_REVIEWS_PER_WEEK`.
 */
export function resolveRequiredShadowReviewsPerWeek(
  configured: number | undefined,
): number {
  return configured !== undefined &&
    Number.isInteger(configured) &&
    configured > 0
    ? configured
    : DEFAULT_REQUIRED_SHADOW_REVIEWS_PER_WEEK;
}

/** The coverage rollup counts a selection decision needs. */
export interface ShadowCoverageCounts {
  /** Reviewers currently assigned to this section-week. */
  assignedCount: number;
  /** Assigned reviewers who have submitted feedback. */
  submittedCount: number;
  /** Whether an as-yet-unfulfilled review is wanted (set by the reviewee). */
  reviewRequested: boolean;
  /** Reviewer uids already assigned (for dedupe). */
  reviewerUids: string[];
  /**
   * For each unsubmitted required claim holding one of this section-week's
   * credit slots, the epoch ms at which its hold lapses (see
   * {@link countTakenCreditSlots}). Empty when none holds one.
   */
  requiredHoldsUntilMs: number[];
}

/** One shadowable section-week, annotated for selection. */
export interface ShadowCandidate {
  /** Section being reviewed. */
  sectionId: SectionId;
  /** 1-based section week being reviewed. */
  sectionWeek: SectionWeekId;
  /** The section leader who taught this week (never assigned to review themselves). */
  revieweeUid: string;
  /** True when this week is in the course's guaranteed-coverage set. */
  isGuaranteedWeek: boolean;
  /**
   * True once this section-week can actually be reviewed: the section's
   * scheduled slot has finished and its recording exists (see
   * `readShadowCandidates`).
   */
  isShadowable: boolean;
  /**
   * Epoch ms the section's scheduled slot ends for this week, or null when the
   * section or course is untimed.
   */
  scheduledEndMs: number | null;
  /** Current coverage rollup, or null when no coverage doc exists yet. */
  coverage: ShadowCoverageCounts | null;
}

/**
 * A candidate wants a review when its week is guaranteed or a review was
 * explicitly requested. Guaranteed weeks are always wanted (every section is
 * covered); other weeks only when the reviewee asked.
 */
function isWanted(candidate: ShadowCandidate): boolean {
  return (
    candidate.isGuaranteedWeek || (candidate.coverage?.reviewRequested ?? false)
  );
}

/**
 * Whether a reviewer could take this candidate at all: it is wanted, not their
 * own section, and not one they already review. Says nothing about whether it
 * is shadowable yet.
 */
function isOpenToReviewer(
  candidate: ShadowCandidate,
  reviewerUid: string,
): boolean {
  return (
    candidate.revieweeUid !== reviewerUid &&
    !(candidate.coverage?.reviewerUids.includes(reviewerUid) ?? false) &&
    isWanted(candidate)
  );
}

/** Returns one item uniformly at random, or null for an empty list. */
function pickRandom<T>(items: T[], random: () => number): T | null {
  return items[Math.floor(random() * items.length)] ?? null;
}

/**
 * Counts a section-week's taken credit slots. Only a review that takes a slot
 * counts toward its reviewer's requirement. Each submitted review takes one,
 * and each unsubmitted required claim holds one on the half-open interval
 * `[claim, holdUntil)`: at `holdUntil` itself the hold has lapsed.
 *
 * @param coverage - The section-week's coverage rollup, or null when no
 *   coverage doc exists yet (nothing claimed or submitted).
 * @param nowMs - Current time (epoch ms).
 * @return The number of slots taken or held at `nowMs`.
 */
export function countTakenCreditSlots({
  coverage,
  nowMs,
}: {
  coverage: ShadowCoverageCounts | null;
  nowMs: number;
}): number {
  if (coverage === null) return 0;
  return (
    coverage.submittedCount +
    coverage.requiredHoldsUntilMs.filter(untilMs => nowMs < untilMs).length
  );
}

/**
 * Whether a section-week has a credit slot free for another required claim
 * (see {@link countTakenCreditSlots}).
 *
 * @param coverage - The section-week's coverage rollup, or null when no
 *   coverage doc exists yet (every slot is free).
 * @param nowMs - Current time (epoch ms).
 * @param slotCount - Credit slots each section-week has: the course's required
 *   reviews per week. Omitted ⇒ `DEFAULT_REQUIRED_SHADOW_REVIEWS_PER_WEEK`.
 * @return True when a required claim may take a slot.
 */
export function isCreditSlotFree({
  coverage,
  nowMs,
  slotCount = DEFAULT_REQUIRED_SHADOW_REVIEWS_PER_WEEK,
}: {
  coverage: ShadowCoverageCounts | null;
  nowMs: number;
  slotCount?: number;
}): boolean {
  return countTakenCreditSlots({coverage, nowMs}) < slotCount;
}

/**
 * Picks the section a reviewer takes as a required review, among the
 * shadowable, wanted candidates that are not their own, that they do not
 * already review, and that have a credit slot free (see
 * {@link isCreditSlotFree}). Sections with the fewest slots taken come first,
 * so every section gets a first review before any gets a second. Candidates
 * tied on that are chosen between at random, which spreads concurrent
 * claimers across sections instead of sending them all to the same one.
 *
 * @param candidates - Candidates in the weeks the reviewer still owes.
 * @param reviewerUid - The section leader claiming.
 * @param nowMs - Current time (epoch ms), for the credit-slot holds.
 * @param random - Source of randomness returning a value in `[0, 1)`.
 * @param slotCount - Credit slots each section-week has. Omitted ⇒
 *   `DEFAULT_REQUIRED_SHADOW_REVIEWS_PER_WEEK`.
 * @return The chosen candidate, or null when no credit slot is free for them.
 */
export function pickRequiredCandidate({
  candidates,
  reviewerUid,
  nowMs,
  random,
  slotCount = DEFAULT_REQUIRED_SHADOW_REVIEWS_PER_WEEK,
}: {
  candidates: ShadowCandidate[];
  reviewerUid: string;
  nowMs: number;
  random: () => number;
  slotCount?: number;
}): ShadowCandidate | null {
  const taken = (candidate: ShadowCandidate) =>
    countTakenCreditSlots({coverage: candidate.coverage, nowMs});
  const eligible = candidates.filter(
    candidate =>
      candidate.isShadowable &&
      isOpenToReviewer(candidate, reviewerUid) &&
      taken(candidate) < slotCount,
  );
  const fewestTaken = Math.min(...eligible.map(taken));
  return pickRandom(
    eligible.filter(candidate => taken(candidate) === fewestTaken),
    random,
  );
}

/** Sections that are not shadowable yet but may still open a credit slot. */
export interface PendingCreditSlots {
  /** How many such sections there are for the reviewer. */
  count: number;
  /**
   * Earliest scheduled end still ahead of `nowMs` among them, or null when
   * none is ahead (they have all ended and are waiting on a recording, or are
   * untimed).
   */
  nextSectionEndMs: number | null;
}

/**
 * Summarizes the sections a reviewer may yet be able to take as a required
 * review: wanted candidates that are not their own, that they do not already
 * review, that have a credit slot free, and that are not shadowable yet.
 *
 * @param candidates - Candidates in the weeks the reviewer still owes.
 * @param reviewerUid - The section leader claiming.
 * @param nowMs - Current time (epoch ms). A scheduled end counts as ahead on
 *   the open interval `(nowMs, ∞)`.
 * @param slotCount - Credit slots each section-week has. Omitted ⇒
 *   `DEFAULT_REQUIRED_SHADOW_REVIEWS_PER_WEEK`.
 * @return The count of such sections and the next scheduled end among them.
 */
export function summarizePendingCreditSlots({
  candidates,
  reviewerUid,
  nowMs,
  slotCount = DEFAULT_REQUIRED_SHADOW_REVIEWS_PER_WEEK,
}: {
  candidates: ShadowCandidate[];
  reviewerUid: string;
  nowMs: number;
  slotCount?: number;
}): PendingCreditSlots {
  const pending = candidates.filter(
    candidate =>
      !candidate.isShadowable &&
      isOpenToReviewer(candidate, reviewerUid) &&
      isCreditSlotFree({coverage: candidate.coverage, nowMs, slotCount}),
  );
  const upcomingEnds = pending
    .map(candidate => candidate.scheduledEndMs)
    .filter((endMs): endMs is number => endMs !== null && endMs > nowMs);
  return {
    count: pending.length,
    nextSectionEndMs:
      upcomingEnds.length > 0 ? Math.min(...upcomingEnds) : null,
  };
}

/**
 * Picks the section a reviewer should shadow as an opt-in extra: the
 * most-uncovered shadowable, wanted candidate that isn't their own section and
 * that they don't already review. "Most uncovered" is fewest *completed*
 * (submitted) reviews first, then fewest assigned — so a section with no
 * finished review wins over one that merely has more reviewers assigned.
 * Candidates tied on both counts are chosen between at random, so concurrent
 * claimers spread across them.
 *
 * @param candidates - All shadowable candidates for the course.
 * @param reviewerUid - The section leader opting in.
 * @param random - Source of randomness returning a value in `[0, 1)`.
 * @return The chosen candidate, or null when nothing is eligible.
 */
export function pickClaimCandidate({
  candidates,
  reviewerUid,
  random,
}: {
  candidates: ShadowCandidate[];
  reviewerUid: string;
  random: () => number;
}): ShadowCandidate | null {
  const eligible = candidates.filter(
    candidate =>
      candidate.isShadowable && isOpenToReviewer(candidate, reviewerUid),
  );
  const submitted = (candidate: ShadowCandidate) =>
    candidate.coverage?.submittedCount ?? 0;
  const assigned = (candidate: ShadowCandidate) =>
    candidate.coverage?.assignedCount ?? 0;
  const fewestSubmitted = Math.min(...eligible.map(submitted));
  const leastReviewed = eligible.filter(
    candidate => submitted(candidate) === fewestSubmitted,
  );
  const fewestAssigned = Math.min(...leastReviewed.map(assigned));
  return pickRandom(
    leastReviewed.filter(candidate => assigned(candidate) === fewestAssigned),
    random,
  );
}

/**
 * One of a reviewer's `extra`-source assignments, reduced to what
 * {@link pickExtraToCount} needs.
 */
export interface ReviewerExtraAssignment {
  /** The assignment's document id. */
  assignmentId: string;
  /** 1-based section week the extra review is of. */
  sectionWeek: SectionWeekId;
  /** The reviewer submitted feedback for it. */
  isSubmitted: boolean;
}

/**
 * Picks the extra review that should count toward a week the reviewer still
 * owes once that week has run out of credit slots for them: a submitted extra
 * of that week if they have one, otherwise one still in progress.
 *
 * @param extraAssignments - The reviewer's `extra`-source assignments.
 * @param sectionWeek - The owed week to find an extra in.
 * @return The extra to count, or null when they have none in that week.
 */
export function pickExtraToCount({
  extraAssignments,
  sectionWeek,
}: {
  extraAssignments: ReviewerExtraAssignment[];
  sectionWeek: SectionWeekId;
}): ReviewerExtraAssignment | null {
  const inWeek = extraAssignments.filter(
    assignment => assignment.sectionWeek === sectionWeek,
  );
  return (
    inWeek.find(assignment => assignment.isSubmitted) ?? inWeek.at(0) ?? null
  );
}

/**
 * One of a reviewer's `required`-source assignments, reduced to what the
 * per-week requirement calc needs. Callers pass only `required` assignments
 * (opt-in `extra` ones never bear on the requirement) and map the assignment
 * status onto these two booleans, keeping this module free of the status enum.
 */
export interface ReviewerRequiredAssignment {
  /** 1-based guaranteed week this required assignment covers. */
  sectionWeek: SectionWeekId;
  /** The reviewer submitted feedback for it — fulfills the week. */
  isSubmitted: boolean;
  /** It is an active (claimed, not yet submitted or expired) hold on the week. */
  isActive: boolean;
}

/** A reviewer's standing against the per-guaranteed-week review requirement. */
export interface ReviewerRequirement {
  /** Total required reviews: the per-week number for each guaranteed week. */
  required: number;
  /**
   * Required reviews submitted so far that count: at most the per-week number
   * for each guaranteed week.
   */
  completed: number;
  /** Guaranteed weeks with the full per-week number of submitted required reviews. */
  completedWeeks: SectionWeekId[];
  /**
   * Guaranteed weeks short of the per-week number that have an active
   * (claimed) required review.
   */
  inProgressWeeks: SectionWeekId[];
  /**
   * Guaranteed weeks short of the per-week number with no active required
   * review: the weeks a required claim draws from. A week holds one active
   * required review at a time, so a week needing several is owed again each
   * time one is submitted.
   */
  owedWeeks: SectionWeekId[];
}

/**
 * Computes a reviewer's per-week review requirement: they owe
 * `requiredPerWeek` reviews for each guaranteed-coverage week, and only a
 * `required`-source review of a section in that same week counts toward it (a
 * week-1 review never covers week 2, and opt-in `extra` reviews never count).
 * This is the demand signal shown on the SL home that drives the pull-based
 * supply.
 *
 * @param guaranteedWeeks - The course's guaranteed-coverage weeks (the obligations).
 * @param requiredAssignments - The reviewer's `required`-source assignments; the
 *   caller filters out `extra` ones before calling.
 * @param requiredPerWeek - Required reviews per guaranteed week. Omitted ⇒
 *   `DEFAULT_REQUIRED_SHADOW_REVIEWS_PER_WEEK`.
 * @return The requirement breakdown: totals, and completed, in-progress, and
 *   owed weeks.
 */
export function computeReviewerRequirement({
  guaranteedWeeks,
  requiredAssignments,
  requiredPerWeek = DEFAULT_REQUIRED_SHADOW_REVIEWS_PER_WEEK,
}: {
  guaranteedWeeks: SectionWeekId[];
  requiredAssignments: ReviewerRequiredAssignment[];
  requiredPerWeek?: number;
}): ReviewerRequirement {
  const submitted = new Map<number, number>();
  const active = new Set<number>();
  for (const assignment of requiredAssignments) {
    if (assignment.isSubmitted) {
      submitted.set(
        assignment.sectionWeek,
        (submitted.get(assignment.sectionWeek) ?? 0) + 1,
      );
    } else if (assignment.isActive) {
      active.add(assignment.sectionWeek);
    }
  }
  const isComplete = (week: SectionWeekId) =>
    (submitted.get(week) ?? 0) >= requiredPerWeek;
  return {
    required: guaranteedWeeks.length * requiredPerWeek,
    completed: guaranteedWeeks.reduce(
      (total, week) =>
        total + Math.min(requiredPerWeek, submitted.get(week) ?? 0),
      0,
    ),
    completedWeeks: guaranteedWeeks.filter(isComplete),
    inProgressWeeks: guaranteedWeeks.filter(
      week => !isComplete(week) && active.has(week),
    ),
    owedWeeks: guaranteedWeeks.filter(
      week => !isComplete(week) && !active.has(week),
    ),
  };
}
